import { useReceivedInvitations, useAcceptInvitation, useRejectInvitation } from '../hooks/useCoOwnershipInvitations';
import type { InvitationStatus } from '../types/coOwnershipInvitation';

const STATUS_STYLES: Record<InvitationStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-gray-100 text-gray-700',
  REVOKED: 'bg-red-100 text-red-700',
  INVALIDATED: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS: Record<InvitationStatus, string> = {
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  REVOKED: 'Revoked',
  INVALIDATED: 'Invalidated',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

const MyInvitations = () => {
  const { data: invitations, isLoading, isError } = useReceivedInvitations();
  const acceptMutation = useAcceptInvitation();
  const rejectMutation = useRejectInvitation();

  const actionErrors: Record<string, string> = {};

  const handleAccept = async (apartmentId: string, token: string) => {
    try {
      await acceptMutation.mutateAsync({ apartmentId, token });
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      actionErrors[token] = detail ?? 'Something went wrong, please try again';
    }
  };

  const handleReject = async (apartmentId: string, token: string) => {
    try {
      await rejectMutation.mutateAsync({ apartmentId, token });
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      actionErrors[token] = detail ?? 'Something went wrong, please try again';
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Invitations</h1>

      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map(n => (
            <div key={n} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-600 text-sm">Failed to load invitations. Please try again.</p>
        </div>
      )}

      {!isLoading && !isError && invitations?.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <p className="text-gray-500">You have no invitations</p>
        </div>
      )}

      {!isLoading && !isError && invitations && invitations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
          {invitations.map(inv => (
            <div key={inv.id} className="flex items-center justify-between gap-4 p-4 flex-wrap">
              <div className="space-y-0.5 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {inv.apartmentId}
                </p>
                <p className="text-xs text-gray-500">
                  From {inv.invitingOwnerSub}
                </p>
                <p className="text-xs text-gray-400">Sent {formatDate(inv.createdAt)}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[inv.status]}`}>
                  {STATUS_LABELS[inv.status]}
                </span>
                {inv.status === 'PENDING' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAccept(inv.apartmentId, inv.token)}
                      disabled={acceptMutation.isPending}
                      className="py-1.5 px-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {acceptMutation.isPending ? '…' : 'Accept'}
                    </button>
                    <button
                      onClick={() => handleReject(inv.apartmentId, inv.token)}
                      disabled={rejectMutation.isPending}
                      className="py-1.5 px-3 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {rejectMutation.isPending ? '…' : 'Reject'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyInvitations;
